'use client';

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { RushMidErrorScreen } from './RushMidErrorScreen';

interface Props { children: ReactNode }
interface State { error: Error | null }

/**
 * Catches any render/effect throw in the RushMid tree (loading + game) and shows a visible,
 * recoverable error screen instead of a BLACK SCREEN — the app has no other error boundary,
 * so an uncaught throw (e.g. a memory-starved Android WebView failing canvas getContext /
 * getImageData while preloading sprites) would otherwise unmount the whole tree to black.
 *
 * componentDidCatch receives the REAL error in-app — not the cross-origin-masked
 * "Script error." that leaks to window.onerror — so this is also how we finally SEE the
 * cause: it logs the message + component stack to the console (captured by Datadog RUM) and
 * reports an explicit RUM error tagged `rush-mid`.
 */
export class RushMidErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[rush-mid] crashed:', error, info.componentStack);
    void import('@datadog/browser-rum')
      .then(({ datadogRum }) => datadogRum.addError(error, { feature: 'rush-mid', componentStack: info.componentStack }))
      .catch(() => { /* RUM not initialised / offline — the console error still reaches RUM */ });
  }

  render(): ReactNode {
    const { error } = this.state;
    if (error) {
      // show the actual message + a slice of the stack so a failing device reveals the cause
      // on screen (not just a black void); both buttons let the user retry / hard-reload.
      const detail = [error.message || 'Unknown error', error.stack?.split('\n')[1]?.trim()].filter(Boolean).join('\n');
      return <RushMidErrorScreen message={detail} onBack={() => this.setState({ error: null })} />;
    }
    return this.props.children;
  }
}
